import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StarRating } from '@/components/ui/star-rating';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';
import { useCreateReviewMutation } from '@/services/api/services/servicesApi';
import type { CreateReviewRequest } from '@/services/api/services/servicesApi';
import { useToast } from '@/components/ui/toast-provider';

export interface WriteReviewModalProps {
  serviceId: string;
  serviceName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const WriteReviewModal: React.FC<WriteReviewModalProps> = ({
  serviceId,
  serviceName,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [rating, setRating] = React.useState<number>(0);
  const [title, setTitle] = React.useState<string>('');
  const [comment, setComment] = React.useState<string>('');
  const [errors, setErrors] = React.useState<{ rating?: string; title?: string; comment?: string }>({});

  const [createReview, { isLoading, error }] = useCreateReviewMutation();
  const { showToast } = useToast();

  const validateForm = (): boolean => {
    const newErrors: { rating?: string; title?: string; comment?: string } = {};

    if (rating === 0) {
      newErrors.rating = 'Please select a rating';
    }

    if (title.trim().length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    if (comment.trim().length > 500) {
      newErrors.comment = 'Comment must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const reviewData: CreateReviewRequest = {
        rating,
        title: title.trim() || undefined,
        comment: comment.trim() || undefined,
      };

      await createReview({ serviceId, review: reviewData }).unwrap();
      
      // Reset form
      setRating(0);
      setTitle('');
      setComment('');
      setErrors({});
      
      // Show success toast
      showToast({
        type: 'success',
        title: 'Review submitted successfully!',
        message: 'Thank you for sharing your experience.',
      });
      
      // Call success callback
      onSuccess?.();
      
      // Close modal
      onClose();
    } catch (err) {
      console.error('Failed to create review:', err);
      
      // Show error toast
      showToast({
        type: 'error',
        title: 'Failed to submit review',
        message: 'Please try again later.',
      });
    }
  };

  const handleClose = () => {
    setRating(0);
    setTitle('');
    setComment('');
    setErrors({});
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg mx-4">
        <Card>
          <CardHeader className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2"
              onClick={handleClose}
              disabled={isLoading}
            >
              <X className="h-4 w-4" />
            </Button>
            <CardTitle className="text-xl">Write a Review</CardTitle>
            <CardDescription>
              Share your experience with {serviceName}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Star Rating */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Your Rating *
                </Label>
                <div className="flex items-center gap-2">
                  <StarRating
                    rating={rating}
                    interactive={true}
                    onRatingChange={setRating}
                    size="lg"
                  />
                  <span className="text-sm text-muted-foreground">
                    {rating > 0 ? `${rating} star${rating > 1 ? 's' : ''}` : 'Click to rate'}
                  </span>
                </div>
                {errors.rating && (
                  <p className="text-sm text-red-600">{errors.rating}</p>
                )}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-medium">
                  Review Title (Optional)
                </Label>
                <input
                  type="text"
                  id="title"
                  placeholder="Summarize your experience in a few words..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={100}
                  disabled={isLoading}
                />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    {title.length}/100 characters
                  </span>
                  {errors.title && (
                    <p className="text-sm text-red-600">{errors.title}</p>
                  )}
                </div>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label htmlFor="comment" className="text-sm font-medium">
                  Your Review (Optional)
                </Label>
                <textarea
                  id="comment"
                  placeholder="Tell others about your experience with this service..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full min-h-[120px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  maxLength={500}
                  disabled={isLoading}
                />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    {comment.length}/500 characters
                  </span>
                  {errors.comment && (
                    <p className="text-sm text-red-600">{errors.comment}</p>
                  )}
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">
                    Failed to submit review. Please try again.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || rating === 0}
                  className="flex-1"
                >
                  {isLoading ? 'Submitting...' : 'Submit Review'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export { WriteReviewModal }; 
